import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { SettingsSidebar } from './SettingsSidebar';
import { SettingsAccordion } from './SettingsAccordion';
import { ProfileSection } from './ProfileSection';
import { AccountSection } from './AccountSection';
import { PrivacySection } from './PrivacySection';
import { SubscriptionSection } from './SubscriptionSection';
import { NotificationSection } from './NotificationSection';
import { GeneralSection } from './GeneralSection';
import { DisplaySection } from './DisplaySection';
import { useTheme } from '../ThemeContext';
interface ProfileSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  userType?: 'angel' | 'customer';
}
export const ProfileSettings: React.FC<ProfileSettingsProps> = ({
  isOpen,
  onClose,
  userType = 'customer'
}) => {
  const {
    theme
  } = useTheme();
  const [activeSection, setActiveSection] = useState('profile');
  const [isMobile, setIsMobile] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  // Check if screen is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  // Handle close with unsaved changes
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      onClose();
    }
  };
  // Render the active section content
  const renderSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSection setHasUnsavedChanges={setHasUnsavedChanges} data-id="element-18852" />;
      case 'account':
        return <AccountSection setHasUnsavedChanges={setHasUnsavedChanges} data-id="element-18853" />;
      case 'privacy':
        return <PrivacySection setHasUnsavedChanges={setHasUnsavedChanges} data-id="element-18854" />;
      case 'subscription':
        return userType === 'angel' ? <SubscriptionSection setHasUnsavedChanges={setHasUnsavedChanges} data-id="element-18855" /> : <div className="p-6 text-center text-gray-500 dark:text-gray-400" data-id="element-18856">
            Subscription settings are only available for Angel accounts.
          </div>;
      case 'notifications':
        return <NotificationSection setHasUnsavedChanges={setHasUnsavedChanges} data-id="element-18857" />;
      case 'general':
        return <GeneralSection setHasUnsavedChanges={setHasUnsavedChanges} data-id="element-18858" />;
      case 'display':
        return <DisplaySection setHasUnsavedChanges={setHasUnsavedChanges} data-id="element-18859" />;
      default:
        return <ProfileSection setHasUnsavedChanges={setHasUnsavedChanges} data-id="element-18860" />;
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-18861">
      <div className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 shadow-xl animate-in zoom-in-90 duration-300" data-id="element-18862">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900" data-id="element-18863">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-18864">
            Profile Settings
          </h2>
          <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors" onClick={handleClose} aria-label="Close settings" data-id="element-18865">
            <X size={18} className="text-gray-500 dark:text-gray-400" data-id="element-18866" />
          </button>
        </div>
        <div className="flex h-[70vh] overflow-hidden" data-id="element-18867">
          {/* Sidebar (desktop) or Accordion (mobile) */}
          {isMobile ? <SettingsAccordion activeSection={activeSection} setActiveSection={setActiveSection} userType={userType} data-id="element-18868" /> : <SettingsSidebar activeSection={activeSection} setActiveSection={setActiveSection} userType={userType} data-id="element-18869" />}
          {/* Content Area */}
          <div className="flex-1 overflow-y-auto" data-id="element-18870">{renderSection()}</div>
        </div>
        {/* Footer/Actions */}
        <div className="sticky bottom-0 z-10 flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900" data-id="element-18871">
          <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm transition-colors text-gray-700 dark:text-gray-300" onClick={handleClose} data-id="element-18872">
            Cancel
          </button>
          <div className="flex items-center gap-3" data-id="element-18873">
            <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm transition-colors text-gray-700 dark:text-gray-300" onClick={() => {
            // Reset current section
            const event = new CustomEvent('resetSettingsSection', {
              detail: {
                section: activeSection
              }
            });
            document.dispatchEvent(event);
          }} data-id="element-18874">
              Reset
            </button>
            <button className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${hasUnsavedChanges ? 'bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700' : 'bg-gray-400 dark:bg-gray-700 cursor-not-allowed'}`} disabled={!hasUnsavedChanges} onClick={() => {
            // Save current section
            const event = new CustomEvent('saveSettingsSection', {
              detail: {
                section: activeSection
              }
            });
            document.dispatchEvent(event);
            setHasUnsavedChanges(false);
          }} data-id="element-18875">
              Save Changes
            </button>
          </div>
        </div>
        {/* Unsaved Changes Modal */}
        {showUnsavedChangesModal && <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50" data-id="element-18876">
            <div className="bg-white dark:bg-gray-900 p-6 rounded-xl max-w-md w-full" data-id="element-18877">
              <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white" data-id="element-18878">
                Unsaved Changes
              </h3>
              <p className="mb-4 text-gray-700 dark:text-gray-300" data-id="element-18879">
                You have unsaved changes. Are you sure you want to leave without
                saving?
              </p>
              <div className="flex justify-end gap-3" data-id="element-18880">
                <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm" onClick={() => setShowUnsavedChangesModal(false)} data-id="element-18881">
                  Cancel
                </button>
                <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm" onClick={() => {
              setShowUnsavedChangesModal(false);
              onClose();
            }} data-id="element-18882">
                  Discard Changes
                </button>
              </div>
            </div>
          </div>}
      </div>
    </div>;
};